import { motion, Variants } from 'framer-motion';
import React from 'react';
import { isStyledComponent } from 'styled-components';
import Modal from '../Modal/modal';
import { AnimationProps, GetAnimationProps, ModalWithAnimationProps } from './modalWithAnimation.types';

const COMPONENT_KEY = 'modal-with-animation';
export const CONTENT_VARIANT_OVERRIDES = {
  hide: { y: 24 },
  show: { y: 0, transition: { delay: 0.2 } },
};
const DEFAULT_DATA_ATTR = { 'data-testid': COMPONENT_KEY };
const DEFAULT_DATA_ATTR_BACKDROP = {
  'data-testid': `${COMPONENT_KEY}__backdrop`,
};
const DEFAULT_DATA_ATTR_CLOSE_BUTTON = {
  'data-testid': `${COMPONENT_KEY}__close-button`,
};
const DEFAULT_DATA_ATTR_CONTENT = {
  'data-testid': `${COMPONENT_KEY}__content-container`,
};
const DEFAULT_VARIANTS = {
  hide: {
    opacity: 0,
  },
  show: {
    opacity: 1,
  },
};

export const getAnimationProps = ({
  as = motion.div,
  isStyledComponent,
  key,
  variantOverrides = {},
}: GetAnimationProps): AnimationProps => {
  const variants: Variants = { ...DEFAULT_VARIANTS };

  /** Currently only supporting `hide` and `show` properties */
  if ('hide' in variantOverrides) {
    variants.hide = { ...variants.hide, ...variantOverrides.hide };
  }

  if ('show' in variantOverrides) {
    variants.show = { ...variants.show, ...variantOverrides.show };
  }

  const animationProps: AnimationProps = {
    key,
    initial: 'hide',
    animate: 'show',
    exit: 'hide',
    variants,
  };

  /**
   * The Modal subcomponents (Backdrop, ModalContent) can be replaced, and it is feasible that they may be
   * replaced with styled components, or the same components but wrapped and customized.
   * - e.g., const CustomModalContent = styled(ModalContent)``;
   * In such cases, `forwardedAs` is necessary to ensure it will be passed along to the wrapped component.
   * Reference: https://styled-components.com/docs/api#forwardedas-prop
   */
  if (isStyledComponent) {
    animationProps.forwardedAs = as;
  } else {
    animationProps.as = as;
  }

  return animationProps;
};

const ModalWithAnimation = ({
  BackdropComponent,
  backdropProps = {},
  closeButtonProps = {},
  ContentComponent,
  contentProps = {},
  dataAttr,
  portalProps,
  ...props
}: ModalWithAnimationProps) => {
  const animatedBackdropProps = getAnimationProps({
    isStyledComponent: isStyledComponent(BackdropComponent),
    key: `${COMPONENT_KEY}__backdrop`,
  });
  const animatedContentProps = getAnimationProps({
    isStyledComponent: isStyledComponent(ContentComponent),
    key: `${COMPONENT_KEY}__content`,
    variantOverrides: CONTENT_VARIANT_OVERRIDES,
  });
  const { dataAttr: backdropDataAttr, ...restBackdropProps } = backdropProps;
  const combinedBackdropProps = {
    dataAttr: { ...DEFAULT_DATA_ATTR_BACKDROP, ...backdropDataAttr },
    ...animatedBackdropProps,
    ...restBackdropProps,
  };
  const { dataAttr: closeButtonDataAttr, ...restCloseButtonProps } = closeButtonProps;
  const combinedCloseButtonProps = {
    dataAttr: { ...DEFAULT_DATA_ATTR_CLOSE_BUTTON, ...closeButtonDataAttr },
    ...restCloseButtonProps,
  };
  const { dataAttr: contentDataAttr, ...restContentProps } = contentProps;
  const combinedContentProps = {
    dataAttr: { ...DEFAULT_DATA_ATTR_CONTENT, ...contentDataAttr },
    ...animatedContentProps,
    ...restContentProps,
  };
  const combinedDataAttr = { ...DEFAULT_DATA_ATTR, ...dataAttr };
  const combinedPortalProps = { ...portalProps, isTransitionable: true };
  return (
    <Modal
      BackdropComponent={BackdropComponent}
      backdropProps={combinedBackdropProps}
      closeButtonProps={combinedCloseButtonProps}
      ContentComponent={ContentComponent}
      contentProps={combinedContentProps}
      dataAttr={combinedDataAttr}
      portalProps={combinedPortalProps}
      {...props}
    />
  );
};

export default React.memo(ModalWithAnimation);
